import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldDefinition, FieldType } from '@up2d8/contracts';
import { LoadingState, useToast } from '@up2d8/ui';
import {
  ActionsCell,
  AdminIcon,
  AdminPage,
  Badge,
  Btn,
  Card,
  CardPad,
  Empty,
  Field,
  Form,
  IconBtn,
  Input,
  Modal,
  PageHead,
  Row,
  Segmented,
  Table,
  Toggle,
  useAdminToast,
} from '../admin-kit';
import { ConfirmModal } from '../organisatie/confirm-modal';
import { localized } from '../../shared/format';
import { useSession } from '../../shared/session';
import { nextFieldKey, suggestFieldKey } from './field-key';
import { ListError } from '../../shared/list-error';

const TYPES: FieldType[] = ['text', 'number', 'date', 'select'];

/**
 * Gegevensvelden: de kolommen die dit bedrijf zelf aan zijn medewerkers hangt (O4, O5).
 *
 * De sleutel en het type liggen na het aanmaken vast; dat staat er ook bij, zodat niemand
 * zich er later op verkijkt.
 */
export function FieldsPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const toast = useToast();
  const adminToast = useAdminToast();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bewerken, setBewerken] = useState<FieldDefinition | null>(null);
  const [verwijderen, setVerwijderen] = useState<FieldDefinition | null>(null);

  const fields = useQuery({
    queryKey: ['directory', 'fields'],
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/fields');
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as { items: FieldDefinition[] }).items;
    },
  });

  /**
   * Een veld tonen aan collega's, of niet meer (SCH-3).
   *
   * Geen optimistische update: dit gaat over wie wat van wie ziet, en een schakelaar die vast
   * lijkt te staan terwijl de server het weigerde, is op dit scherm het verkeerde soort fout.
   */
  const setVisible = useMutation({
    mutationFn: async ({ id, visible }: { id: string; visible: boolean }) => {
      const { error } = await api.PATCH('/api/v1/fields/{fieldId}', {
        params: { path: { fieldId: id } },
        body: { visibleToColleagues: visible } as never,
      });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['directory', 'fields'] }),
    onError: () => toast.show({ tone: 'danger', title: t('fields.visibleFailed') }),
  });

  const create = useMutation({
    mutationFn: async (input: {
      key: string;
      label: Record<string, string>;
      type: FieldType;
      options?: string[];
    }) => {
      const { error } = await api.POST('/api/v1/fields', { body: input as never });
      if (error) throw error;
    },
    onSuccess: async () => {
      setDialogOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['directory'] });
    },
  });

  /**
   * Label en keuzeopties bewerken (punt 6).
   *
   * De sleutel en het type blijven vast: bestaande waarden zouden anders van betekenis veranderen.
   * Dat stond al zo in het contract; wat ontbrak, was de weg ernaartoe in het scherm.
   */
  const update = useMutation({
    mutationFn: async (input: {
      fieldId: string;
      label: Record<string, string>;
      options?: string[];
    }) => {
      const { error } = await api.PATCH('/api/v1/fields/{fieldId}', {
        params: { path: { fieldId: input.fieldId } },
        body: { label: input.label, options: input.options } as never,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      setBewerken(null);
      adminToast.show(t('common.saved'));
      await queryClient.invalidateQueries({ queryKey: ['directory'] });
    },
    // De server weigert een optie die nog in gebruik is, mét het aantal erbij. Die zin is voor de
    // beheerder geschreven, dus tonen we hem en niet onze eigen samenvatting ervan.
    onError: (error) =>
      toast.show({
        tone: 'danger',
        title: (error as { detail?: string }).detail ?? t('fields.updateFailed'),
      }),
  });

  const remove = useMutation({
    mutationFn: async (fieldId: string) => {
      const { data, error } = await api.DELETE('/api/v1/fields/{fieldId}', {
        params: { path: { fieldId } },
      });
      if (error) throw error;
      return data as { result: 'deleted' | 'archived' };
    },
    onSuccess: async (result) => {
      adminToast.show(result.result === 'deleted' ? t('fields.deleted') : t('fields.archived'));
      await queryClient.invalidateQueries({ queryKey: ['directory'] });
    },
    onError: () => toast.show({ title: t('fields.removeFailed'), tone: 'danger' }),
  });

  const nieuwKnop = (variant?: 'sec') => (
    <Btn variant={variant} icon={<AdminIcon name="plus" />} onClick={() => setDialogOpen(true)}>
      {t('fields.new')}
    </Btn>
  );

  return (
    <AdminPage>
      <PageHead
        title={t('fields.title')}
        sub={t('beheer.organisatie.fields.sub')}
        actions={nieuwKnop()}
      />

      <Card>
        {fields.isPending ? (
          <CardPad>
            <LoadingState rows={4} label={t('common.loading')} />
          </CardPad>
        ) : fields.isError ? (
          <CardPad>
            <ListError onRetry={() => void fields.refetch()} error={fields.error} />
          </CardPad>
        ) : (fields.data?.length ?? 0) === 0 ? (
          <Empty
            icon={<AdminIcon name="tag" size={24} />}
            title={t('fields.empty')}
            text={t('beheer.organisatie.fields.emptyText')}
            action={nieuwKnop('sec')}
          />
        ) : (
          <Table label={t('fields.title')}>
            <thead>
              <tr>
                <th>{t('fields.label')}</th>
                <th>{t('fields.key')}</th>
                <th>{t('fields.type')}</th>
                <th>{t('beheer.organisatie.fields.usedBy')}</th>
                <th>{t('fields.visibleToColleagues')}</th>
                <th className="act" aria-label={t('common.actions')} />
              </tr>
            </thead>
            <tbody>
              {fields.data?.map((field) => {
                const naam = localized(field.label) ?? field.key;
                return (
                  <tr key={field.id}>
                    <td>
                      <span className="a-name">{naam}</span>
                    </td>
                    <td>
                      <span className="a-mono">{field.key}</span>
                    </td>
                    <td>
                      <Badge tone="info">{t(`fields.types.${field.type}`)}</Badge>
                      {field.type === 'select' ? (
                        <div className="a-meta">{(field.options ?? []).join(', ')}</div>
                      ) : null}
                    </td>
                    <td>{t('beheer.organisatie.fields.usedCount', { count: field.valueCount })}</td>
                    <td>
                      {/*
                        Zichtbaar voor collega's (SCH-3, ADR-0069).

                        Standaard uit, en dat blijft zo tot iemand hier klikt: een veld als
                        "loonschaal" hoort niet op de vloer te komen omdat er een scherm bijkwam.
                        De uitleg staat erbij en niet in een helpbestand — wie dit aanzet, hoort
                        op dat moment te weten wat het betekent.
                      */}
                      <div className="flex items-start gap-[10px]">
                        <Toggle
                          checked={field.visibleToColleagues}
                          label={t('beheer.organisatie.fields.visibleFor', { name: naam })}
                          testId="field-visible"
                          disabled={setVisible.isPending}
                          onChange={(visible) => setVisible.mutate({ id: field.id, visible })}
                        />
                        <span className="a-hint">{t('fields.visibleHint')}</span>
                      </div>
                    </td>
                    <ActionsCell>
                      {field.isSystem ? (
                        <Badge>{t('fields.system')}</Badge>
                      ) : (
                        <>
                          <IconBtn
                            label={t('common.edit')}
                            icon={<AdminIcon name="pencil" />}
                            testId="field-edit"
                            onClick={() => setBewerken(field)}
                          />
                          {/*
                            Verwijderen en archiveren vragen eerst (punt 13). Een veld met waarden
                            wordt gearchiveerd en niet gewist - maar ook dat verdwijnt uit elk
                            formulier, en dat merk je pas als iemand het de volgende keer zoekt.
                          */}
                          <IconBtn
                            label={field.valueCount > 0 ? t('fields.archive') : t('fields.delete')}
                            icon={<AdminIcon name="trash" />}
                            testId="field-remove"
                            onClick={() => setVerwijderen(field)}
                          />
                        </>
                      )}
                    </ActionsCell>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <NewFieldModal
        open={dialogOpen}
        onClose={() => {
          // Anders staat de fout van de vorige poging er nog bij het volgende openen.
          create.reset();
          setDialogOpen(false);
        }}
        busy={create.isPending}
        failed={create.isError}
        onSubmit={(input) => create.mutate(input)}
      />

      {bewerken ? (
        <EditFieldModal
          /* Een sleutel per veld: anders houdt het venster de waarden van het vorige veld vast. */
          key={bewerken.id}
          field={bewerken}
          busy={update.isPending}
          onClose={() => setBewerken(null)}
          onSubmit={(input) => update.mutate({ fieldId: bewerken.id, ...input })}
        />
      ) : null}

      <ConfirmModal
        open={verwijderen !== null}
        onClose={() => setVerwijderen(null)}
        title={(verwijderen?.valueCount ?? 0) > 0 ? t('fields.archive') : t('fields.delete')}
        description={
          (verwijderen?.valueCount ?? 0) > 0
            ? t('fields.archiveConfirm', {
                name: localized(verwijderen?.label) ?? verwijderen?.key,
                count: verwijderen?.valueCount ?? 0,
              })
            : t('fields.deleteConfirm', {
                name: localized(verwijderen?.label) ?? verwijderen?.key,
              })
        }
        confirmLabel={(verwijderen?.valueCount ?? 0) > 0 ? t('fields.archive') : t('fields.delete')}
        busy={remove.isPending}
        testId="confirm-field-remove"
        onConfirm={() => {
          if (verwijderen) remove.mutate(verwijderen.id);
          setVerwijderen(null);
        }}
      />

      {adminToast.element}
    </AdminPage>
  );
}

/**
 * De drie talen van een label naast elkaar (O5). NL is verplicht, FR en EN mogen leeg blijven:
 * dan geldt de Nederlandse naam.
 */
function LabelVelden({
  waarde,
  onChange,
}: {
  waarde: Record<string, string>;
  onChange: (waarde: Record<string, string>) => void;
}) {
  const { t } = useTranslation();
  const talen = [
    ['nl', t('beheer.organisatie.fields.nameNl')],
    ['fr', t('beheer.organisatie.fields.nameFr')],
    ['en', t('beheer.organisatie.fields.nameEn')],
  ] as const;
  return (
    <Row three>
      {talen.map(([taal, label]) => (
        <Field key={taal} label={label} optional={taal !== 'nl'}>
          {(props) => (
            <Input
              {...props}
              data-testid={`field-label-${taal}`}
              value={waarde[taal] ?? ''}
              onChange={(event) => onChange({ ...waarde, [taal]: event.target.value })}
            />
          )}
        </Field>
      ))}
    </Row>
  );
}

/** De keuzeopties van een veld van het type Keuze, gescheiden door komma of nieuwe regel. */
function OptiesVeld({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <Field label={t('fields.options')} hint={t('fields.optionsHint')}>
      {(props) => (
        <Input
          {...props}
          data-testid={testId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
    </Field>
  );
}

function optieLijst(options: string): string[] {
  return options
    .split(/[\n,]/)
    .map((option) => option.trim())
    .filter(Boolean);
}

/** Bewerken: alleen het label en de keuzeopties. Sleutel en type liggen vast. */
function EditFieldModal({
  field,
  busy,
  onClose,
  onSubmit,
}: {
  field: FieldDefinition;
  busy: boolean;
  onClose: () => void;
  onSubmit: (input: { label: Record<string, string>; options?: string[] }) => void;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState<Record<string, string>>(field.label as Record<string, string>);
  const [options, setOptions] = useState((field.options ?? []).join(', '));

  return (
    <Modal
      open
      wide
      onClose={onClose}
      title={t('fields.edit')}
      sub={t('fields.editHint', { key: field.key })}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          <Btn
            testId="field-save"
            disabled={busy || !(label.nl ?? '').trim()}
            onClick={() =>
              onSubmit({
                label: Object.fromEntries(
                  Object.entries(label).filter(([, waarde]) => waarde.trim() !== ''),
                ),
                options: field.type === 'select' ? optieLijst(options) : undefined,
              })
            }
          >
            {t('common.save')}
          </Btn>
        </>
      }
    >
      <LabelVelden waarde={label} onChange={setLabel} />
      {field.type === 'select' ? (
        <OptiesVeld value={options} onChange={setOptions} testId="field-options" />
      ) : null}
    </Modal>
  );
}

/** Veld toevoegen (O5): een breed venster met de drie namen naast elkaar. */
function NewFieldModal({
  open,
  onClose,
  busy,
  failed,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  busy: boolean;
  failed: boolean;
  onSubmit: (input: {
    key: string;
    label: Record<string, string>;
    type: FieldType;
    options?: string[];
  }) => void;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState<Record<string, string>>({});
  const [key, setKey] = useState('');
  const [keyHandmatig, setKeyHandmatig] = useState(false);
  const [type, setType] = useState<FieldType>('text');
  const [options, setOptions] = useState('');

  /*
    Leegmaken zodra het venster sluit (punt 15).

    Zonder dit staat de vorige invoer er nog bij de volgende "Veld toevoegen", inclusief de
    handmatig aangepaste sleutel - en dan maakt iemand per ongeluk twee keer hetzelfde veld, of
    erger: eentje met de sleutel van het vorige.
  */
  useEffect(() => {
    if (open) return;
    setLabel({});
    setKey('');
    setKeyHandmatig(false);
    setType('text');
    setOptions('');
  }, [open]);

  const optionList = optieLijst(options);
  const geldig =
    (label.nl ?? '').trim() !== '' &&
    key.length >= 2 &&
    !(type === 'select' && optionList.length === 0);

  return (
    <Modal
      open={open}
      wide
      onClose={onClose}
      title={t('fields.new')}
      footer={
        <>
          <Btn variant="sec" onClick={onClose}>
            {t('common.cancel')}
          </Btn>
          {/* Zie de opmerking bij "Groep toevoegen": de knop hoort bij het formulier hierboven. */}
          <Btn
            form="nieuw-veld"
            type="submit"
            icon={<AdminIcon name="plus" />}
            testId="field-create"
            disabled={busy || !geldig}
          >
            {t('fields.new')}
          </Btn>
        </>
      }
    >
      <Form
        id="nieuw-veld"
        onSubmit={() => {
          if (!geldig) return;
          onSubmit({
            key,
            label: Object.fromEntries(
              Object.entries(label)
                .map(([taal, waarde]) => [taal, waarde.trim()])
                .filter(([, waarde]) => waarde !== ''),
            ),
            type,
            options: type === 'select' ? optionList : undefined,
          });
        }}
      >
        <LabelVelden
          waarde={label}
          onChange={(waarde) => {
            setLabel(waarde);
            // De sleutel volgt de **Nederlandse** naam, want die is verplicht - maar alleen tot de
            // gebruiker hem zelf aanraakt (punt 4).
            setKey((huidig) =>
              nextFieldKey({ label: waarde.nl ?? '', key: huidig, handmatig: keyHandmatig }),
            );
          }}
        />
        <Field
          label={t('fields.key')}
          hint={t('beheer.organisatie.fields.keyHint')}
          error={failed ? t('fields.createFailed') : undefined}
        >
          {(props) => (
            <Input
              {...props}
              data-testid="field-key"
              value={key}
              onChange={(event) => {
                setKeyHandmatig(true);
                setKey(suggestFieldKey(event.target.value));
              }}
            />
          )}
        </Field>
        <div className="a-field">
          <div className="a-label" id="nieuw-veld-type">
            {t('fields.type')}
          </div>
          <Segmented
            labelledBy="nieuw-veld-type"
            value={type}
            onChange={setType}
            options={TYPES.map((option) => ({ value: option, label: t(`fields.types.${option}`) }))}
          />
          <div className="a-hint">{t('beheer.organisatie.fields.typeHint')}</div>
        </div>
        {type === 'select' ? <OptiesVeld value={options} onChange={setOptions} /> : null}
      </Form>
    </Modal>
  );
}
